import {AxiosError, AxiosResponse} from "axios";
import {Dispatch, SetStateAction, useState} from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Modal from "react-bootstrap/Modal";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {CopyToClipboard} from "../common/CopyToClipboardButton";
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {useRenderOnce} from "../hook/UseRenderOnce";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";

/**
 * The owner should handle the OnClose event. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause: DialogCloseCause) => void;

interface TfaSetupWizardProps {
    initFunc : () => Promise<AxiosResponse<void>>
    is2FAEnabledFunc : () => Promise<AxiosResponse<boolean>>
    getSecretQRCodeFunc : () => Promise<AxiosResponse<string>>
    getSecretFunc : () => Promise<AxiosResponse<string>>
    set2FAEnabledFunc : (enabled : boolean) => Promise<AxiosResponse<void>>
    validateOTPFunc : (otpCode : string) => Promise<AxiosResponse<void>>
    onClose: OnClose
}

interface WizardPage {
    id: "disable" | "intro" | "download" | "qrcode" | "validate" | "finish"
    title: string
}

interface DisableProps {
    disable: boolean
    setDisable: Dispatch<SetStateAction<boolean>>
}

/**
 * Disable wizard page component
 */
const Disable = ({disable, setDisable} : DisableProps) => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    return (
        <div>
            <p>{t('2fa-setup-wizard.disable-text1')}</p>
            <p>{t('2fa-setup-wizard.disable-text2')}</p>
            <RenderAlert message={t('2fa-setup-wizard.disable-alert')} variant="warning"/>

            <Form.Group>
                <Form.Check type="radio"
                            checked={disable}
                            onChange={() => setDisable(true)}
                            label="Disable"

                />
                <Form.Check type="radio"
                            checked={!disable}
                            onChange={() => setDisable(false)}
                            label="Keep enabled"
                />
            </Form.Group>
        </div>
    )
}

/**
 * Intro wizard page component
 */
const Intro = () => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    return (
        <div>
            <p>{t('2fa-setup-wizard.intro-content')}</p>
            <p>{t('2fa-setup-wizard.intro-extra')}</p>
            <ol>
                <li>{t('2fa-setup-wizard.intro-step1')}</li>
                <li>{t('2fa-setup-wizard.intro-step2')}</li>
                <li>{t('2fa-setup-wizard.intro-step3')}</li>
            </ol>
        </div>
    )
}

/**
 * Download wizard page component
 */
const Download = () => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    return (
        <div>
            <p className='mb-3'>{t('2fa-setup-wizard.download-intro')}</p>
            <p>{t('2fa-setup-wizard.download-content')}</p>
            <ul>
                <li>Google Authenticator
                    <ul>
                        <li>Android: <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noreferrer">Google Play</a></li>
                        <li>IOS: <a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="noreferrer">App Store</a></li>
                    </ul>
                </li>
                <li>Microsoft Authenticator
                    <ul>
                        <li>Android: <a href="https://play.google.com/store/apps/details?id=com.azure.authenticator" target="_blank" rel="noreferrer">Google Play</a></li>
                        <li>IOS: <a href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458" target="_blank" rel="noreferrer">App Store</a></li>
                    </ul>
                </li>
            </ul>
            {t('2fa-setup-wizard.download-continue')}
        </div>
    )
}

interface QRCodeProps {
    qrCode: string | undefined
    secretKey: string | undefined
}

/**
 * QR-Code wizard page component
 */
const QRCode = ({qrCode, secretKey}: QRCodeProps) =>
{
    /**
     * Set to true when a value is copied to the clipboard
     */
    const [copied, setCopied] = useState<boolean>(false);

    const {t} = useTranslation('components');

    return (
        <div>
            <p>{t('2fa-setup-wizard.scan-qr-code-text1')}</p>
            <img src={`data:image/png;base64,${qrCode}`} alt="QR-code"/>
            <p>{t('2fa-setup-wizard.scan-qr-code-text2')}</p>

            <p>{t('2fa-setup-wizard.secret-key-copy-paste-text')}</p>
            <p>
                <InputGroup>
                    <FloatingLabel label={t('2fa-setup-wizard.secret-key-label')}
                                   className="text-secondary mb-3">
                        <Form.Control type="text" value={secretKey} placeholder="SecretKey" disabled={true}
                                      readOnly={true}/>
                    </FloatingLabel>
                    <InputGroup.Text className="border-0 d-inline">
                        <CopyToClipboard value={secretKey} setCopied={setCopied}/>
                    </InputGroup.Text>
                </InputGroup>
                {copied && <Alert className="text-break"
                                  variant={"success"}>{t('common.copied-to-clipboard')}</Alert>}
            </p>
        </div>
    )
}

interface ValidateProps {
    otpCode: string
    setOtpCode: Dispatch<SetStateAction<string>>
}

/**
 * Validate wizard page component
 */
const Validate = ({otpCode, setOtpCode}: ValidateProps) => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    return (
        <div>
            <p>{t('2fa-setup-wizard.validate-intro')}</p>

            <FloatingLabel label={t('2fa-setup-wizard.validate-label')} className="text-secondary mt-3">
                <Form.Control type="text"
                              value={otpCode}
                              placeholder="Enter the code generated by the Authenticator App"
                              onChange={e => setOtpCode(e.target.value)}/>
            </FloatingLabel>
        </div>
    )
}

const Finish = () => {
    const {t} = useTranslation('components');

    return (
        <div>
            <p>
                <p>{t('2fa-setup-wizard.finish-text1')}</p>
                <p>{t('2fa-setup-wizard.finish-text2')}</p>
                <p>{t('2fa-setup-wizard.finish-text3')}</p>
                <RenderAlert message={t('2fa-setup-wizard.finish-alert')} variant="warning"/>
            </p>
        </div>
    )
}

export const TfaSetupWizard = ({
                                   initFunc,
                                   is2FAEnabledFunc,
                                   getSecretQRCodeFunc,
                                   getSecretFunc,
                                   set2FAEnabledFunc,
                                   validateOTPFunc,
                                   onClose}: TfaSetupWizardProps) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The current wizard index
     */
    const [wizardIndex, setWizardIndex] = useState<number>(0);
    /**
     * True if TFA is enabled
     */
    const [isTFAEnabled, setIsTFAEnabled] = useState<boolean>(false);
    /**
     * True if TFA should be disabled
     */
    const [disable, setDisable] = useState<boolean>(false);
    /**
     * The base64 encoded QR-code containing the 2FA secret
     */
    const [secretKeyQRCode, setSecretKeyQRCode] = useState<string>("");
    /**
     * The 2FA secret
     */
    const [secretKey, setSecretKey] = useState<string>("");
    /**
     * The TOTP code provided by the user
     */
    const [otpCode, setOtpCode] = useState<string>("");
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    /**
     * The Wizard pages
     */
    const [wizardPages] = useState<WizardPage[]>(
        [
            {id: "disable", title: t('2fa-setup-wizard.disable-title')},
            {id: "intro", title: t('2fa-setup-wizard.intro-title')},
            {id: "download", title: t('2fa-setup-wizard.download-title')},
            {id: "qrcode", title: t('2fa-setup-wizard.scan-qr-code-title')},
            {id: "validate", title: t('2fa-setup-wizard.validate-title')},
            {id: "finish", title: t('2fa-setup-wizard.finish-title')},
        ]
    );

    /**
     * Check if TFA is enabled
     */
    useRenderOnce(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        is2FAEnabledFunc().then(response =>
        {
            setIsTFAEnabled(response.data);

            if (!response.data) {
                setWizardIndex(1);
            }
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error checking TFA enabled status", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    });

    useRenderOnce(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // init first because this might generate a TFA secret, then load TFA secret
        initFunc().then(() =>
        {
            getSecretFunc().then(response => {
                setSecretKey(response.data);
            })

            getSecretQRCodeFunc().then(response => {
                setSecretKeyQRCode(response.data);
            })
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error initializing TFA ", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    });

    // called when the dialog is closed without clicking download
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const currentPage = () => {
        return wizardPages[wizardIndex];
    }

    const isLastPage = () => {
        return wizardIndex === (wizardPages.length - 1)
    }

    const isFirstPage = () => {
        return wizardIndex === 0
    }

    const nextPage = () => {
        if (isLastPage()) {
            throw Error("Last page reached")
        }

        setWizardIndex(index => index + 1)
    }

    const previousPage = () => {
        if (isFirstPage()) {
            throw Error("At first page")
        }

        setWizardIndex(index => index - 1)
    }

    const cancel = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const enableTFA = (enable : boolean) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        set2FAEnabledFunc(enable).then(() => {
                onClose(DialogCloseCause.Succes);
            })
            .catch((err: Error | AxiosError) =>
            {
                logger.error("Error enabling 2FA", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }

    const finish = () => {
        enableTFA(!disable);
    }

    const isNextDisabled = () => {
        return !!(isLastPage() || (currentPage().id === "validate" && StringUtils.isBlank(otpCode) || disable));
    }

    const isPreviousDisabled = () => {
        return isFirstPage() || (currentPage().id === "intro" && !isTFAEnabled);
    }

    const isFinishDisabled = () => {
        return !(isLastPage() || (currentPage().id === "disable" && disable));
    }

    const validateOTPCode = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        validateOTPFunc(otpCode).then(() => {})
            .then(() => {
                nextPage();
            })
            .catch((err: Error | AxiosError) =>
            {
                logger.error("Error validating OTP Code", err);

                if (err instanceof AxiosError && err.response?.status === 401) {
                    setErrorMessage(t('2fa-setup-wizard.validate-incorrect-otp-code'))
                }
                else {
                    setErrorMessage(Utils.getErrorMessage(err, t));
                }
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }

    const onClickNext = () =>
    {
        setErrorMessage(undefined);

        if (currentPage().id === "validate")
        {
            validateOTPCode();
            // if validation succeeds, the next page will be activated by validateOTPCode
            return;
        }

        nextPage();
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" scrollable={false} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{currentPage().title}</Modal.Title>
            </Modal.Header>
            <div className="row flex-grow-1">
                <main className="col d-flex flex-column overflow-auto vh-80">
                    <Modal.Body>
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>
                        <Form>
                            {currentPage().id === "disable" && <Disable disable={disable} setDisable={setDisable}/>}
                            {currentPage().id === "intro" && <Intro/>}
                            {currentPage().id === "download" && <Download/>}
                            {currentPage().id === "qrcode" && <QRCode qrCode={secretKeyQRCode} secretKey={secretKey}/>}
                            {currentPage().id === "validate" &&
                                <Validate otpCode={otpCode} setOtpCode={setOtpCode}/>}
                            {currentPage().id === "finish" && <Finish/>}
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" disabled={isPreviousDisabled()} onClick={previousPage}>
                            {t('wizard.previous-button')}
                        </Button>
                        <Button variant="primary" disabled={isNextDisabled()} onClick={onClickNext}>
                            {t('wizard.next-button')}
                        </Button>
                        <Button variant="danger" onClick={cancel}>
                            {t('wizard.cancel-button')}
                        </Button>
                        <Button variant="success" disabled={isFinishDisabled()} onClick={finish}>
                            {t('wizard.finish-button')}
                        </Button>
                    </Modal.Footer>
                </main>
            </div>
        </Modal>
    )
}
